import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { FaEdit, FaTrash } from "react-icons/fa";
import { ListGroup, Table } from "react-bootstrap";
import { cdiscManagementService } from "../../../APIServices/CDISC/cdiscManagementService";
import { NoDataFoundTable, PageLoader } from "../../../Components/NoDataFoundTable/NoDataFoundTable";
import Swal from "sweetalert2";
import Moment from "react-moment";
import SplitPane from "react-split-pane";
import Pagination from "../../Pagination";
import AddTrialDesign from "./AddTrialDesign";
import UseToggle from "../../../Hooks/UseToggle";
import UpdateTrialDesign from "./UpdateTrialDesign";
import ImportTrialDesignColumnData from "./ImportTrialDesignColumnData";

const TrialDesignManagement = ({ onMouseDown, tableRef }) => {
  const toggles = UseToggle();
  const [loading, setLoading] = useState(false);
  const [libraryList, setFormLibraryList] = useState([]);
  const [trailId, setTrailId] = useState("");
  const [trailName, setTrailName] = useState("");
  const { studyId } = useParams();
  const [buttonLoader, setButtonLoader] = useState(false);

  const [libraryTemplate, setFormLibraryTemplate] = useState([]);
  const [loader, setLoader] = useState(false);

  const [studyDataEntry, setStudyDataEntry] = useState([]);
  const [studyDataEntryCount, setStudyDataEntryCount] = useState(0);
  const [templateData, setTemplateData] = useState(null);
  const [dataLoader, setDataLoader] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const startIndex = (pageNumber - 1) * itemsPerPage;
  const totalPage = Math.ceil(studyDataEntryCount / itemsPerPage);

  const getCDISCTrailDesignData = async () => {
    try {
      setLoading(true);
      const data = await cdiscManagementService.getCDISCTrailDesignData(
        "active",
      );
      setFormLibraryList(data?.data || []);
    } finally {
      setLoading(false);
    }
  };

  const downloadTrialDesignTemplate = async () => {
    const csvContent = (libraryTemplate?.map(item => item?.name)).join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Trial-Design-Template.csv";
    link.click();
  };

  useEffect(() => {
    getCDISCTrailDesignData();
  }, []);

  const getCDISCTrailDesignColumnsData = async () => {
    if (!trailId) return;
    try {
      setLoader(true);
      const data = await cdiscManagementService.getCDISCTrailDesignColumnsData(
        "active",
        trailId
      );
      setFormLibraryTemplate(data?.data || []);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getCDISCTrailDesignColumnsData();
  }, [trailId]);

  const getCDISCStudyTrailDesignRow = async () => {
    try {
      setDataLoader(true);
      const data = await cdiscManagementService.getCDISCStudyTrailDesignRow(
        trailId,
        studyId,
        "",
        pageNumber,
        itemsPerPage
      );
      setStudyDataEntry(data?.data || []);
      setStudyDataEntryCount(data?.count || 0);
    } finally {
      setDataLoader(false);
    }
  };

  useEffect(() => {
    if (trailId) {
      getCDISCStudyTrailDesignRow();
    }
  }, [trailId, pageNumber, itemsPerPage]);


  const deleteCDISCStudyTrailDesignRow = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#103C5E",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const userData = { id: id, studyId };
        cdiscManagementService.deleteCDISCStudyTrailDesignRow(userData);
       getCDISCStudyTrailDesignRow();
      }
    });
  };

  const pullCDISCStudyTrailDesignRow = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: `You want to pull ${trailName} trial design?`,
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, pull it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setButtonLoader(true);
        const userData = {};
        userData.studyId = studyId;
        userData.trailId = trailId;
        const data = await cdiscManagementService.pullCDISCStudyTrailDesignRow(
          userData
        );
        if (data?.statusCode === 200) {
          getCDISCStudyTrailDesignRow();
        }
        setButtonLoader(false);
      }
    });
  };

  return (
    <>
      <SplitPane
        split="vertical"
        minSize={"20%"}
        className="position-relative"
        style={{ height: "calc(100vh - 145px)" }}
      >
        {loading ? (
          <PageLoader />
        ) : libraryList?.length === 0 ? (
          <NoDataFoundTable MSG={"Form library list not found!"} />
        ) : (
          <div className="p-2">
            <ListGroup>
              {libraryList.map((item, index) => (
                <ListGroup.Item
                  action
                  title={item?.name}
                  onClick={() => {
                    setTrailId(item?.trailId);
                    setTrailName(item?.name);
                    setPageNumber(1);
                  }}
                  key={item?._id || index}
                  className={`hstack justify-content-between ${
                    trailId === item?.trailId && "selectedMenu"
                  }`}
                >
                  <span>{item?.name}</span>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}

        {loader || dataLoader ? (
          <PageLoader />
        ) : !trailId ? (
          <NoDataFoundTable MSG={"Select any library!"} />
        ) : (
          <>
            <div
              className="hstack justify-content-between border-bottom py-1 px-2 toolbar"
              style={{ height: 32 }}
            >
              <div className="hstack gap-2">
                <i
                  className="fa-solid fa-plus-square CP"
                  title="Add Data"
                  onClick={() => {toggles.setToggle()}}
                />
                <div className="vr" />
                <i
                  className="fa-solid fa-code-pull-request"
                  title={`Pull ${trailName} trial data`}
                  onClick={pullCDISCStudyTrailDesignRow}
                />
              </div>
              <button
                onClick={() => toggles.setToggle4()}
                className={`updateProfile`}
                style={{ fontSize: 12 }}
              >
                Import
              </button>
            </div>
            <SplitPane
              split="vertical"
              minSize={"100%"}
              className="position-relative"
              style={{ height: "calc(100vh - 178px)" }}
            >
              {studyDataEntryCount === 0 ? (
                <NoDataFoundTable MSG={"No Data Found!"} />
              ) : (
                <>
                  <Table
                    ref={tableRef}
                    className="m-0 custom-table w-auto"
                    hover
                    striped
                  >
                    <thead className="position-sticky top-0">
                      <tr>
                        <th
                          onMouseDown={(e) => onMouseDown(e, 0)}
                          className="text-center"
                          style={{ width: 40 }}
                        >
                          Sr.
                        </th>
                        {libraryTemplate?.map((item, index) => (
                          <th
                            key={index}
                            onMouseDown={(e) => onMouseDown(e, index + 1)}
                          >
                            {item?.label}
                          </th>
                        ))}
                        <th>Updated By</th>
                        {/* <th>Created Date | Time</th> */}
                        <th>Updated Date | Time</th>
                        <th className="text-center" style={{ width: 60 }}>
                          Edit
                        </th>
                        <th className="text-center" style={{ width: 60 }}>
                          Delete
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {studyDataEntry?.map((item, index) => (
                        <tr key={item._id || index}>
                          <td className="text-center">{startIndex + index + 1}</td>
                          {libraryTemplate.map((columnData, key) => (
                            <td key={key}>{item?.dataEntry?.[columnData?.name]}</td>
                          ))}
                          <td>{item?.updatedBy}</td>
                          {/* <td>
                            <Moment format="DD MMM YYYY | HH:mm" withTitle>
                              {item?.createdAt}
                            </Moment>
                          </td> */}
                          <td>
                            <Moment format="DD MMM YYYY | HH:mm" withTitle>
                              {item?.updatedAt}
                            </Moment>
                          </td>
                          <td
                            className="text-center CP"
                            title={`Update Row`}
                            onClick={() => {
                              setTemplateData(item);
                              toggles.setToggle3();
                            }}
                          >
                            <FaEdit className="text-muted" />
                          </td>
                          <td
                            className="text-center CP"
                            title={`Delete Row`}
                            onClick={() => deleteCDISCStudyTrailDesignRow(item?._id)}
                          >
                            <FaTrash className={"text-danger"} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div className="position-sticky bottom-0 start-0">
                    <Pagination
                      totalPage={totalPage}
                      pageNumber={pageNumber}
                      itemsPerPage={itemsPerPage}
                      totalItems={studyDataEntryCount}
                      setPageNumber={setPageNumber}
                      setItemsPerPage={setItemsPerPage}
                    />
                  </div>
                </>
              )}
            </SplitPane>
          </>
        )}
      </SplitPane>

      <AddTrialDesign
        Show={!toggles.toggle}
        Hide={toggles.setToggle}
        loader={loader}
        trailId={trailId}
        studyId={studyId}
        Title="Add Trial Row"
        libraryTemplate={libraryTemplate}
        getCDISCStudyTrailDesignRow={getCDISCStudyTrailDesignRow}
        getCDISCTrailDesignColumnsData={getCDISCTrailDesignColumnsData}
      />

      <UpdateTrialDesign
        Show={!toggles.toggle3}
        Hide={toggles.setToggle3}
        trailId={trailId}
        studyId={studyId}
        Title="Update Trial Row"
        templateData={templateData}
        libraryTemplate={libraryTemplate}
        getCDISCStudyTrailDesignRow={getCDISCStudyTrailDesignRow}
        getCDISCTrailDesignColumnsData={getCDISCTrailDesignColumnsData}
      />
      <ImportTrialDesignColumnData
        Show={!toggles.toggle4}
        Hide={toggles.setToggle4}
        trailId={trailId}
        studyId={studyId}
        Title={"Import Trail Design Column Data"}
        getCDISCStudyTrailDesignRow={getCDISCStudyTrailDesignRow}
        downloadTrialDesignTemplate={downloadTrialDesignTemplate}
      />
    </>
  );
};

export default TrialDesignManagement;
